import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadDialog from "@/components/LeadDialog";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Factory, TrendingDown, Receipt, Leaf, Wrench, ShieldCheck,
  Zap, BarChart3, CircleCheck, IndianRupee,
} from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const HERO_IMG = "https://images.unsplash.com/photo-1504762300654-109d62172feb?w=2000&q=85&auto=format&fit=crop";

export default function Commercial() {
  const { t } = useTranslation();
  const [leadOpen, setLeadOpen] = useState(false);
  const openQuote = () => setLeadOpen(true);

  const BENEFITS = [
    { icon: TrendingDown, slug: "b1", title: t("com.b1_t"), desc: t("com.b1_d") },
    { icon: Receipt, slug: "b2", title: t("com.b2_t"), desc: t("com.b2_d") },
    { icon: ShieldCheck, slug: "b3", title: t("com.b3_t"), desc: t("com.b3_d") },
    { icon: Leaf, slug: "b4", title: t("com.b4_t"), desc: t("com.b4_d") },
    { icon: Zap, slug: "b5", title: t("com.b5_t"), desc: t("com.b5_d") },
    { icon: Wrench, slug: "b6", title: t("com.b6_t"), desc: t("com.b6_d") },
  ];

  const STATS = [
    { k: "60%+", v: t("com.stat_reduction") },
    { k: "3–4 yrs", v: t("com.stat_payback") },
    { k: "25 yrs", v: t("com.stat_warranty") },
    { k: "₹0", v: t("com.stat_upfront") },
  ];

  const SEGMENTS = [
    { slug: "s1", title: t("com.seg1_t"), points: [t("com.seg1_p1"), t("com.seg1_p2"), t("com.seg1_p3")] },
    { slug: "s2", title: t("com.seg2_t"), points: [t("com.seg2_p1"), t("com.seg2_p2"), t("com.seg2_p3")] },
    { slug: "s3", title: t("com.seg3_t"), points: [t("com.seg3_p1"), t("com.seg3_p2"), t("com.seg3_p3")] },
    { slug: "s4", title: t("com.seg4_t"), points: [t("com.seg4_p1"), t("com.seg4_p2"), t("com.seg4_p3")] },
  ];

  const PROCESS = [
    { n: "01", t: t("com.p1_t"), d: t("com.p1_d") },
    { n: "02", t: t("com.p2_t"), d: t("com.p2_d") },
    { n: "03", t: t("com.p3_t"), d: t("com.p3_d") },
    { n: "04", t: t("com.p4_t"), d: t("com.p4_d") },
    { n: "05", t: t("com.p5_t"), d: t("com.p5_d") },
    { n: "06", t: t("com.p6_t"), d: t("com.p6_d") },
  ];

  const ROI = [
    { k: t("com.roi1_k"), v: t("com.roi1_v") },
    { k: t("com.roi2_k"), v: t("com.roi2_v") },
    { k: t("com.roi3_k"), v: t("com.roi3_v") },
    { k: t("com.roi4_k"), v: t("com.roi4_v") },
  ];

  return (
    <div className="relative bg-white">
      <Navbar onCtaClick={openQuote} />

      <section data-testid="commercial-hero" className="relative pt-[88px] min-h-[80vh] flex items-end overflow-hidden bg-stone-950">
        <img src={HERO_IMG} alt="Industrial rooftop solar" className="absolute inset-0 w-full h-full object-cover opacity-65" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pb-16 md:pb-24 text-white">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1.5 text-[11px] font-bold tracking-[0.18em] uppercase">
            <Factory className="w-3.5 h-3.5" /> {t("com.hero_badge")}
          </div>
          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
            {t("com.hero_title_a")} <span className="text-orange-400">{t("com.hero_title_hl")}</span> {t("com.hero_title_b")}<br />
            {t("com.hero_title_c")}
          </h1>
          <p className="mt-5 text-white/85 max-w-2xl text-lg leading-relaxed">{t("com.hero_subtitle")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={openQuote} data-testid="commercial-cta-quote" className="btn-premium rounded-full bg-orange-500 hover:bg-orange-600 text-white h-12 px-7 font-semibold">
              {t("com.cta_quote")}<ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
            <Link to="/#calculator" data-testid="commercial-cta-calc" className="inline-flex items-center justify-center rounded-full h-12 px-7 border border-white/30 bg-white/5 text-white font-semibold hover:bg-white/10 transition" reloadDocument>
              {t("com.cta_calc")}
            </Link>
          </div>
        </div>
      </section>

      <section className="border-y border-stone-200 bg-stone-50">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={i} data-testid={`commercial-stat-${i}`}>
              <div className="font-display font-extrabold text-3xl md:text-4xl text-green-700 leading-none">{s.k}</div>
              <div className="mt-2 text-stone-600 text-sm leading-snug">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("com.b_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("com.b_title_a")} <span className="text-green-700">{t("com.b_title_hl")}</span>
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.slug} data-testid={`commercial-benefit-${b.slug}`} className="premium-card group p-7 rounded-3xl border border-stone-200 bg-white hover:border-green-300 hover:premium-shadow transition">
                  <div className="w-12 h-12 rounded-2xl bg-green-100 grid place-items-center group-hover:bg-green-600 transition">
                    <Icon className="w-6 h-6 text-green-700 group-hover:text-white transition" strokeWidth={2.2} />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-xl text-stone-900">{b.title}</h3>
                  <p className="mt-3 text-stone-600 leading-relaxed text-[15px]">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-stone-50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("com.seg_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("com.seg_title_a")} <span className="text-green-700">{t("com.seg_title_hl")}</span> {t("com.seg_title_b")}
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {SEGMENTS.map((s) => (
              <div key={s.slug} data-testid={`commercial-segment-${s.slug}`} className="p-7 rounded-3xl bg-white border border-stone-200">
                <h3 className="font-display font-bold text-2xl text-stone-900">{s.title}</h3>
                <ul className="mt-4 space-y-2.5">
                  {s.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-stone-700 text-[15px]">
                      <CircleCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" strokeWidth={2.4} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <span className="text-orange-600 font-semibold tracking-[0.18em] text-xs uppercase">{t("com.roi_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900 leading-tight">
              {t("com.roi_title_a")} <span className="text-green-700">{t("com.roi_title_hl")}</span>
            </h2>
            <p className="mt-5 text-stone-600 leading-relaxed text-[15px]">{t("com.roi_subtitle")}</p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              {ROI.map((r, i) => (
                <div key={i} className="p-5 rounded-2xl border border-stone-200">
                  <div className="font-display font-extrabold text-2xl text-green-700">{r.k}</div>
                  <div className="text-stone-500 text-sm mt-1">{r.v}</div>
                </div>
              ))}
            </div>
            <Button onClick={openQuote} data-testid="commercial-cta-bottom" className="btn-premium mt-9 rounded-full bg-green-600 hover:bg-green-700 text-white h-12 px-7 font-semibold">
              {t("com.roi_cta")}<ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-[28px] overflow-hidden shadow-2xl border border-stone-200">
              <img src="https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=1400&q=85&auto=format&fit=crop" alt="Factory rooftop solar" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-green-700 text-white rounded-2xl p-5 shadow-xl max-w-[260px]">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-85">
                <BarChart3 className="w-4 h-4" /> {t("com.roi_year1")}
              </div>
              <div className="mt-1 font-display font-black text-2xl flex items-center">
                <IndianRupee className="w-5 h-5" />{t("com.roi_savings").replace("₹", "")}
              </div>
              <div className="text-xs opacity-90">{t("com.roi_savings_caption")}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-green-700 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <span className="text-orange-300 font-semibold tracking-[0.18em] text-xs uppercase">{t("com.p_eyebrow")}</span>
            <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl leading-tight">
              {t("com.p_title_a")} <span className="text-orange-300">{t("com.p_title_hl")}</span>
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROCESS.map((p) => (
              <div key={p.n} data-testid={`commercial-process-${p.n}`} className="p-6 rounded-2xl bg-white/5 border border-white/15 backdrop-blur">
                <div className="font-handwritten text-5xl text-orange-300 leading-none">{p.n}</div>
                <h3 className="mt-3 font-display font-bold text-xl">{p.t}</h3>
                <p className="mt-2 text-white/85 leading-relaxed text-sm">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-stone-50">
        <div className="max-w-5xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-5xl text-stone-900 leading-tight">
            {t("com.final_a")} <span className="text-green-700">{t("com.final_hl")}</span>
          </h2>
          <p className="mt-4 text-stone-600 text-lg max-w-2xl mx-auto">{t("com.final_sub")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button onClick={openQuote} data-testid="commercial-cta-final" className="btn-premium rounded-full bg-orange-500 hover:bg-orange-600 text-white h-12 px-7 font-semibold">
              {t("com.final_cta")}<ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
            <Link to="/" className="inline-flex items-center justify-center rounded-full h-12 px-7 border border-stone-300 bg-white text-stone-800 font-semibold hover:bg-stone-100 transition">
              {t("com.back_home")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <LeadDialog
        open={leadOpen}
        onOpenChange={setLeadOpen}
        context={{ segment: "commercial" }}
        title={t("com.lead_title")}
        subtitle={t("com.lead_subtitle")}
        source="commercial_page"
      />
    </div>
  );
}
